import type { ExitTicketScore, FileMap, Mode } from "@buildlab/shared";
import type { MissionStep, Prediction, Tool } from "@buildlab/shared/steps";
import type { Replay } from "@buildlab/shared/arena";

export type { ExitTicketScore };

// Thin client for apps/api. Turns stream back as server-sent events over a POST.

export interface Me {
  /** owner: a TEACHER_EMAILS teacher (changes settings); guest: an outside guest in a demo class (Review 14). */
  /** name comes from the sign-in account; avatar is the profile icon they picked (a sprite id), or null. */
  user: { id: string; name: string; role: "teacher" | "student"; owner?: boolean; guest?: boolean; avatar?: string | null };
  model: string | null;
  /** Feature switches the teacher controls (apps/api/src/settings.ts). */
  features: Record<string, boolean>;
  /** A student's class (null until they enter the code; always null for teachers). */
  class: MyClass | null;
  /** Students accept the class rules once; teachers read as true. */
  rulesAccepted: boolean;
  /** A student's helper coins for today (1 coin = 1 helper message); absent for teachers. */
  coins?: { left: number; total: number };
}
export type ClassKind = "class" | "demo";
export interface MyClass {
  id: string;
  name: string;
  kind?: ClassKind;
  paused: boolean;
  pauseMessage: string | null;
}
export interface AssignmentInfo {
  id: string;
  lesson: number;
  title: string;
  modes: Mode[];
  /** The mode the helper opens in for this lesson (e.g. Ask for L3). */
  startMode?: Mode;
  blurb: string;
  practice: string;
  instructions: string;
  /** Motion-graphic explainers (packages/assignments Explainer); empty until some are made. */
  explainers?: ExplainerInfo[];
  /** The mission as 3–5 self-ticking steps (Review 17, D45). Sent with a project, not on the Home list. */
  steps?: MissionStep[];
  /** Level-ups (decision 101): up to 3 more steps once the steps are done, skipped or submitted. With a project only. */
  levelUps?: MissionStep[];
  /** Predict before play (D47): the Press Play cover's question, in lessons that start from a given program. */
  predict?: Prediction;
  /** The lesson's first class day (YYYY-MM-DD), or null for the Demo (decision 96). */
  opensOn?: string | null;
  /** True for a student before that day: Start shows a 🔒 and the lesson can't be opened. */
  locked?: boolean;
}
/** Where a project's mission is (apps/api/src/steps.ts): steps done, "Skip steps", the Press Play answer. */
export interface StepState {
  done: string[];
  skipped: boolean;
  prediction: string | null;
  /** Submitted at least once: level-ups open (decision 101). */
  submitted?: boolean;
}
/** "Level ups 2/3" in the teacher's views (decision 101); null for lessons without level-ups. */
export type LevelUpCount = { done: number; total: number } | null;
export interface ExplainerInfo {
  id: string;
  title: string;
  src: string;
  kind: "video" | "animation";
  caption: string;
  captions?: string;
  seconds?: number;
}
/** A teacher's lesson plan (GET /api/assignments/:id/teacher-plan, teachers only; packages/assignments teacher-plans.ts). */
export interface TeacherPlanInfo {
  assignmentId: string;
  lesson: number;
  title: string;
  practice: string;
  modes: Mode[];
  instructions: string;
  when: string;
  pattern: string;
  bigIdea: string;
  skill: string;
  make: string;
  meetings: { title: string; flow: { minutes: number; segment: string; what: string }[] }[];
  success: string[];
  watchFor: string[];
  setup: string[];
  discuss: string[];
  stretch?: string;
  /** What the app checks at submit time (the lesson's evidence hooks). */
  evidence: { label: string; bonus: boolean }[];
  /** The exit-ticket questions in the Submit box. */
  reflection: string[];
  explainers: { title: string; seconds?: number }[];
}
/** One event on the CHA school calendar (decision 47). */
export interface SchoolEvent {
  date: string;
  endDate?: string;
  time?: string;
  title: string;
  kind: string;
  group?: string;
}
/** One meeting in the teacher's Google Classroom kit (GET /api/classroom-kit). */
export interface KitMeeting {
  meeting: number;
  date: string;
  title: string;
  body: string;
  url: string;
  assignmentId: string | null;
  /** False for an earlier part of a multi-meeting lesson: not graded, no due date (Paul, Oct 28). */
  graded: boolean;
}
/** Classroom auto-post (decision 94, GET /api/classroom): one lesson meeting's state in Classroom. */
export interface ClassroomMeeting {
  meeting: number;
  date: string;
  title: string;
  /** When Classroom posts it (UTC, RFC 3339): 7:30 am Central on the meeting day. */
  scheduledTime: string | null;
  status: "posted" | "scheduled" | "draft" | "failed" | "not synced" | "past";
  error: string | null;
  /** The assignment's page in Classroom, once created. */
  link: string | null;
  /** False for an earlier part of a multi-meeting lesson (Paul, Oct 28). */
  graded: boolean;
}
export interface ClassroomStatus {
  configured: boolean;
  canChange: boolean;
  connected: boolean;
  course: { id: string; name: string } | null;
  meetings: ClassroomMeeting[];
}
export interface ProjectSummary {
  id: string;
  assignmentId: string | null;
  title: string;
  updatedAt: number;
}
export interface ProjectDetail {
  project: ProjectSummary & { files: FileMap };
  assignment: AssignmentInfo | null;
  /** The tools this person has in this lesson (D46): teachers everything. */
  tools?: Tool[];
  /** Tools to celebrate with "New tool unlocked!" (students, the first time). */
  newTools?: Tool[];
  steps?: StepState;
}
export interface Flag {
  id: number;
  projectId: string;
  projectTitle: string;
  userName: string;
  mode: Mode;
  message: string;
  reason: string;
  note: string;
  createdAt: number;
  /** Something shared with the class that screening held back ("gallery", "class_data") or blocked ("blocked"). */
  shareKind?: string | null;
  /** From a guest in a demo class. */
  demo?: boolean;
}
export interface PastTurn {
  id: number;
  mode: Mode;
  message: string;
  status: TurnResultEvent["status"];
  finalText: string;
  summary: string[];
  tryThis: string[];
  plan?: { steps: string[]; questions: string[] };
  steps: string[];
  checkpointId: number | null;
  createdAt: number;
}
export interface PlayTest {
  test: string;
  plan: string;
  script: string | null;
  /** The page of a multi-page site the check runs on (default index.html). */
  page?: string;
}
export interface Checkpoint {
  id: number;
  reason: string;
  createdAt: number;
}
export interface DiffStat {
  path: string;
  added: number;
  removed: number;
  status: "added" | "modified" | "deleted";
}
export interface InterviewQuestion {
  question: string;
  options: string[];
}
export interface PlanDetails {
  forWhom?: string;
  later: string[];
  screens: { name: string; shows: string; next?: string }[];
  tests: string[];
}
export interface TurnResultEvent {
  status: "done" | "answer" | "plan" | "questions" | "refused" | "step_limit" | "context_limit" | "error";
  finalText: string;
  summary: string[];
  tryThis: string[];
  plan?: { steps: string[]; questions: string[]; details?: PlanDetails };
  /** Plan-mode interview: questions to step through before the plan. */
  interview?: InterviewQuestion[];
  diff: DiffStat[];
  files: FileMap;
  checkpointId: number | null;
  errorMessage?: string;
}
export type TurnEvent =
  | { type: "step"; tool: string; label: string; path?: string }
  | { type: "file"; path: string; content: string | null }
  | { type: "message_delta"; text: string }
  | { type: "plan"; steps: string[]; questions: string[]; details?: PlanDetails }
  | { type: "questions"; questions: InterviewQuestion[] }
  | { type: "done"; summary: string[]; tryThis: string[]; diff: DiffStat[] }
  | { type: "refusal"; category: string | null }
  | { type: "error"; message: string }
  | ({ type: "result" } & TurnResultEvent);

/** The event card picked with "Change this card…" or "Ask about this" (code view). */
export interface CardFocus {
  title: string;
  file: string;
  startLine: number;
  endLine: number;
}
export interface CardLabelResult {
  id: string;
  title: string;
  steps: string[];
  notes: { line: number; text: string }[];
}

/** One grading-hook result (packages/grading). */
export interface EvidenceResult {
  id: string;
  label: string;
  pass: boolean;
  detail: string;
  bonus?: boolean;
}
/** One earlier submission of a project, with its automatic exit-ticket score (decision 86). */
export interface SubmissionHistoryItem {
  id: number;
  submittedAt: number;
  gradedAt: number | null;
  exitTicket: ExitTicketScore;
}
export interface SubmissionStatus {
  questions: string[];
  /** This lesson's required checks right now, with how to pass each (Oct 19). */
  checks: { id: string; tip: string; pass: boolean }[];
  submissions: SubmissionHistoryItem[];
}
export interface SubmissionAssignment {
  id: string;
  lesson: number;
  title: string;
  practice: string;
}
export interface SubmissionListItem {
  id: number;
  projectId: string;
  assignment: SubmissionAssignment;
  projectTitle: string;
  studentName: string;
  submittedAt: number;
  attempts: number;
  score: string | null;
  gradedAt: number | null;
  evidence: { passed: number; total: number };
  /** The automatic exit-ticket score; the teacher's `score` replaces it when set. */
  exitTicket: ExitTicketScore;
  /** From a guest in a demo class. */
  demo?: boolean;
  levelUps?: LevelUpCount;
}
/** Whether the "Brag to your parents?" pop-up shows after Submit (GET .../brag). */
export interface BragOffer {
  offer: boolean;
  reason: "off" | "email" | "not-student" | "no-parents" | "not-latest" | "sent" | "limit" | null;
  message: string | null;
  /** How many parent emails are on file (never the addresses). */
  parents: number;
  preview: { subject: string; first: string; lesson: string; teacher: string } | null;
}
/** A parent link, for the teacher (Submissions page). */
export interface ParentLinkInfo {
  token: string;
  submissionId: number;
  createdAt: number;
  revokedAt: number | null;
  recipients: number;
  sentTo: string[];
  note: string | null;
}
export interface TranscriptTurn {
  id: number;
  mode: Mode;
  message: string;
  status: string;
  finalText: string;
  summary: string[];
  plan?: { steps: string[]; questions?: string[] };
  steps: string[];
  createdAt: number;
}
export interface SubmissionDetail {
  submission: {
    id: number;
    projectId: string;
    assignment: SubmissionAssignment;
    projectTitle: string;
    studentName: string;
    submittedAt: number;
    files: FileMap;
    reflection: { question: string; answer: string }[];
    evidence: EvidenceResult[];
    exitTicket: ExitTicketScore;
    summary: string | null;
    score: string | null;
    comment: string | null;
    gradedAt: number | null;
    demo?: boolean;
    /** Predict before play (D47): the student's answer on the Press Play cover. Shown, never scored. */
    prediction?: { question: string; choices: string[]; choice: string; at: number | null } | null;
    levelUps?: LevelUpCount;
  };
  history: SubmissionHistoryItem[];
  turns: TranscriptTurn[];
}

// gallery: the class gallery entries (apps/api/src/gallery.ts).
export interface GalleryEntry {
  id: string;
  title: string;
  byline: string;
  createdAt: number;
  remixedFrom: { id: string; title: string; byline: string } | null;
  remixCount: number;
  /** The sharer's profile icon (decision 87). */
  avatar?: string | null;
  /** Shared in a demo class (teachers see every class's entries). */
  demo?: boolean;
  /** The class it was shared in (teachers only; "" for a teacher's own share). */
  className?: string;
  /** The shared project's lesson (0 = the Demo), or null for one outside the lessons: the gallery's tabs. */
  lesson: number | null;
}
// Share with the class (decisions 61, 62; apps/api/src/class-share.ts).
export type ClassDataSource = { projectId?: string; galleryId?: string };
export interface MyGalleryEntry {
  id: string;
  title: string;
  byline: string;
  /** live: classmates see it; pending: waiting for the teacher; hidden: the teacher kept it hidden or took it down. */
  status: "live" | "pending" | "hidden";
}
export type HeldShare =
  | { kind: "gallery"; status: string; entry: { id: string; title: string; byline: string; files: FileMap } }
  | { kind: "class_data"; status: string; data?: { key: string; value: unknown } };
/** A family's show link (apps/api/src/show-links.ts). The token is the link's secret. */
export interface ShowLinkInfo {
  token: string;
  projectId: string;
  title: string;
  createdAt: number;
  expiresAt: number;
  revokedAt: number | null;
  state: "active" | "revoked" | "expired";
}

// Live view and Pause class (decision 68, Q; apps/api/src/live-view.ts).
export interface LiveClass {
  id: string;
  name: string;
  kind?: ClassKind;
  paused: boolean;
  pauseMessage: string | null;
}
export interface LiveStudent {
  userId: string;
  name: string;
  /** Their profile icon (a sprite id), or null. */
  avatar?: string | null;
  /** working: did something in the last 2 min (or the helper is running); idle: 2+ min; away: 10+ min or not yet. */
  status: "working" | "idle" | "away";
  lastSeenAt: number | null;
  helperRunning: boolean;
  project: { id: string; title: string; lesson: number | null; assignmentTitle: string | null; levelUps?: LevelUpCount } | null;
  lastMessage: { text: string; mode: string; at: number } | null;
  openFlags: number;
  /** Open flags on messages screening wasn't sure about: the tile shows "Flagged". */
  screenedFlags: number;
  /** Helper messages used today, and today's limit (with any +10s). */
  turnsToday: number;
  turnLimit: number;
}
/** The class's helper spending this month; alert from 80% of the cap. */
export interface ClassBudget {
  spentUsd: number;
  capUsd: number;
  alert: boolean;
}
export interface HelperLimits {
  dailyTurns: number;
  classMonthlyUsd: number;
}
export interface TeacherProjectView {
  student: { id: string; name: string };
  project: { id: string; title: string; assignmentId: string | null; updatedAt: number; files: FileMap };
  assignment: { id: string; lesson: number; title: string } | null;
  helperRunning: boolean;
  turns: PastTurn[];
}

/**
 * A failed request. kind says why a helper message was turned away, so the helper pane can show it
 * kindly: "limit" (daily limit, class budget or too many at once, a 429) or "blocked" (screening, a 422).
 */
export class ApiError extends Error {
  readonly kind?: "limit" | "blocked";
  constructor(message: string, kind?: "limit" | "blocked") {
    super(message);
    this.kind = kind;
  }
}

// Every request carries the Clerk session token when signed in through Clerk (none in local dev).
let getToken: () => Promise<string | null> = async () => null;
export function setTokenGetter(fn: () => Promise<string | null>) {
  getToken = fn;
}
// Local testing only: open the app with ?as=student1 (or ?as=teacher) to act as a test student.
// The server ignores this header everywhere but localhost with DEV_AUTH on.
const devUser = (() => {
  try {
    const as = new URLSearchParams(window.location.search).get("as");
    if (as) sessionStorage.setItem("buildlab-dev-user", as);
    return sessionStorage.getItem("buildlab-dev-user");
  } catch {
    return null;
  }
})();
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  return { ...(token ? { authorization: `Bearer ${token}` } : {}), ...(devUser ? { "x-buildlab-dev-user": devUser } : {}) };
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, { ...init, headers: { "content-type": "application/json", ...(await authHeaders()), ...(init?.headers ?? {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError((body as { error?: string }).error ?? `Request failed (${res.status}).`);
  return body as T;
}

export const api = {
  me: () => call<Me>("/me"),
  assignments: () => call<{ assignments: AssignmentInfo[] }>("/assignments"),
  teacherPlan: (assignmentId: string) => call<{ plan: TeacherPlanInfo }>(`/assignments/${encodeURIComponent(assignmentId)}/teacher-plan`),
  projects: () => call<{ projects: ProjectSummary[] }>("/projects"),
  flags: () => call<{ flags: Flag[] }>("/flags"),
  schoolCalendar: () => call<{ events: SchoolEvent[]; url: string | null }>("/school-calendar"),
  settings: () => call<{ canChange: boolean; features: { key: string; label: string; leadership: boolean; on: boolean }[] }>("/settings"),
  setFeature: (key: string, on: boolean) => call<{ ok: true }>(`/settings/features/${key}`, { method: "PUT", body: JSON.stringify({ on }) }),
  setSchoolCalendar: (body: { text?: string; url?: string }) => call<{ events: SchoolEvent[]; skipped: string[] }>("/school-calendar", { method: "PUT", body: JSON.stringify(body) }),
  turns: (id: string) => call<{ turns: PastTurn[] }>(`/projects/${id}/turns`),
  reportError: (e: { message: string; stack: string; url: string }) => call<{ ok: true }>("/client-errors", { method: "POST", body: JSON.stringify(e) }),
  reviewFlag: (id: number) => call<{ ok: true }>(`/flags/${id}/reviewed`, { method: "POST" }),
  classroomKit: () => call<{ meetings: KitMeeting[] }>("/classroom-kit"),
  classroom: () => call<ClassroomStatus>("/classroom"),
  classroomConnect: () => call<{ url: string }>("/classroom/connect", { method: "POST" }),
  classroomCourses: () => call<{ courses: { id: string; name: string; section: string | null }[] }>("/classroom/courses"),
  classroomSetCourse: (id: string) => call<{ course: { id: string; name: string } }>("/classroom/course", { method: "PUT", body: JSON.stringify({ id }) }),
  classroomSync: () => call<{ results: (Omit<ClassroomMeeting, "date">)[] }>("/classroom/sync", { method: "POST" }),
  classroomDisconnect: () => call<{ ok: true }>("/classroom/disconnect", { method: "POST" }),
  createProject: (assignmentId: string) => call<{ id: string }>("/projects", { method: "POST", body: JSON.stringify({ assignmentId }) }),
  project: (id: string) => call<ProjectDetail>(`/projects/${id}`),
  // Review 17: mission steps the browser saw done, "Skip steps", the Press Play answer; tools already celebrated.
  saveSteps: (id: string, body: { done?: string[]; skipped?: boolean; prediction?: string }) => call<StepState & { earned?: number }>(`/projects/${id}/steps`, { method: "POST", body: JSON.stringify(body) }),
  toolsSeen: (tools: Tool[]) => call<{ ok: true }>("/tools/seen", { method: "POST", body: JSON.stringify({ tools }) }),
  saveFile: (id: string, path: string, content: string) =>
    call<{ ok: true }>(`/projects/${id}/files/${encodeURIComponent(path)}`, { method: "PUT", body: JSON.stringify({ content }) }),
  checkpoints: (id: string) => call<{ checkpoints: Checkpoint[] }>(`/projects/${id}/checkpoints`),
  saveCheckpoint: (id: string, reason: string) => call<{ id: number }>(`/projects/${id}/checkpoints`, { method: "POST", body: JSON.stringify({ reason }) }),
  labels: (id: string, file: string, cards: { id: string; title: string; code: string }[]) =>
    call<{ labels: CardLabelResult[]; cached?: boolean }>(`/projects/${id}/labels`, { method: "POST", body: JSON.stringify({ file, cards }) }),
  writePlan: (id: string, plan: PlanDetails & { mvp: string[] }) => call<{ files: FileMap }>(`/projects/${id}/notes/plan`, { method: "POST", body: JSON.stringify(plan) }),
  writeTestResults: (id: string, results: { text: string; done: boolean }[]) =>
    call<{ files: FileMap }>(`/projects/${id}/notes/tests`, { method: "POST", body: JSON.stringify({ results }) }),
  data: (source: string, params: unknown) => call<{ data: unknown }>("/data", { method: "POST", body: JSON.stringify({ source, params }) }),
  playTests: (id: string) => call<{ tests: PlayTest[]; cached?: boolean }>(`/projects/${id}/play-tests`, { method: "POST" }),
  checkpoint: (id: string, cid: number) => call<{ files: FileMap }>(`/projects/${id}/checkpoints/${cid}`),
  restore: (id: string, cid: number) => call<{ files: FileMap }>(`/projects/${id}/checkpoints/${cid}/restore`, { method: "POST" }),
  // Submit with reflection (FR-24) and the teacher's grading view.
  submission: (id: string) => call<SubmissionStatus>(`/projects/${id}/submission`),
  submit: (id: string, answers: string[]) => call<{ id: number; submittedAt: number; exitTicket: ExitTicketScore }>(`/projects/${id}/submit`, { method: "POST", body: JSON.stringify({ answers }) }),
  /** Teachers: every student's latest exit ticket per lesson as a zip of Markdown files (decision 86). */
  downloadExitTickets: async (opts: { lesson?: number } = {}) => {
    const q = opts.lesson !== undefined ? `?lesson=${opts.lesson}` : "";
    const res = await fetch(`/api/teacher/exit-tickets.zip${q}`, { headers: await authHeaders() });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(body.error ?? `Request failed (${res.status}).`);
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? "exit-tickets.zip";
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return { files: Number(res.headers.get("x-exit-ticket-files") ?? 0), name };
  },
  teacherSubmissions: () => call<{ submissions: SubmissionListItem[] }>("/teacher/submissions"),
  teacherSubmission: (sid: number) => call<SubmissionDetail>(`/teacher/submissions/${sid}`),
  submissionSummary: (sid: number) => call<{ summary: string | null; reason?: string; cached?: boolean }>(`/teacher/submissions/${sid}/summary`, { method: "POST" }),
  gradeSubmission: (sid: number, score: string, comment: string) =>
    call<{ ok: true; gradedAt: number }>(`/teacher/submissions/${sid}/grade`, { method: "PUT", body: JSON.stringify({ score, comment }) }),
  // Brag to your parents (decision 83; apps/api/src/brag.ts). The student never sends or sees addresses.
  bragOffer: (projectId: string, sid: number) => call<BragOffer>(`/projects/${projectId}/submissions/${sid}/brag`),
  sendBrag: (projectId: string, sid: number, note: string) =>
    call<{ ok: true; sent: number }>(`/projects/${projectId}/submissions/${sid}/brag`, { method: "POST", body: JSON.stringify({ note }) }),
  parentLinks: (sid: number) => call<{ links: ParentLinkInfo[]; featureOn: boolean }>(`/teacher/submissions/${sid}/parent-links`),
  revokeParentLink: (token: string) => call<{ ok: true }>(`/teacher/parent-links/${encodeURIComponent(token)}/revoke`, { method: "POST" }),
  // rememberData: saveData values kept with the project (apps/api/src/project-data.ts).
  savedData: (id: string) => call<{ data: Record<string, unknown> }>(`/projects/${id}/saved`),
  saveValue: (id: string, key: string, value: unknown) =>
    call<{ ok: true }>(`/projects/${id}/saved/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ value }) }),
  clearSaved: (id: string) => call<{ ok: true }>(`/projects/${id}/saved`, { method: "DELETE" }),
  // surpriseMe: three twists for the composer's "Surprise me" button (Surprise.tsx).
  surprise: (id: string) => call<{ twists: SurpriseTwist[] }>(`/projects/${id}/surprise`, { method: "POST" }),
  // gallery: the class gallery, sharing, remixing and taking entries down.
  gallery: () => call<{ entries: GalleryEntry[] }>("/gallery"),
  galleryEntry: (id: string) => call<{ entry: GalleryEntry & { files: FileMap } }>(`/gallery/${id}`),
  shareToGallery: (projectId: string, body: { title: string; byline: string }) =>
    call<{ id: string; updated: boolean; status: "live" | "pending" }>(`/projects/${projectId}/gallery`, { method: "POST", body: JSON.stringify(body) }),
  remixGallery: (id: string) => call<{ id: string }>(`/gallery/${id}/remix`, { method: "POST" }),
  removeFromGallery: (id: string) => call<{ ok: true }>(`/gallery/${id}/remove`, { method: "POST" }),
  // Class-shared data (feature classData): shareData/getShared from projects, and the teacher's panel.
  classData: (key: string) => call<{ value: unknown; updatedAt: number | null }>(`/class-data/${encodeURIComponent(key)}`),
  // from: the project (or the classmate's gallery entry being played) the value comes from, so a value
  // screening isn't sure about can wait for the teacher.
  setClassData: (key: string, value: unknown, from: ClassDataSource = {}) =>
    call<{ ok: true; pending?: true }>(`/class-data/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify({ value, ...from }) }),
  classDataList: () => call<{ items: { key: string; value: unknown; updatedAt: number }[] }>("/class-data"),
  wipeClassData: () => call<{ ok: true }>("/class-data", { method: "DELETE" }),
  // Share with the class (decisions 61, 62): the owner's own entry, and the teacher's choice on a held share.
  myGalleryEntry: (projectId: string) => call<{ entry: MyGalleryEntry | null }>(`/projects/${projectId}/gallery`),
  classShare: (flagId: number) => call<HeldShare>(`/class-share/${flagId}`),
  approveClassShare: (flagId: number) => call<{ ok: true }>(`/class-share/${flagId}/approve`, { method: "POST" }),
  hideClassShare: (flagId: number) => call<{ ok: true }>(`/class-share/${flagId}/hide`, { method: "POST" }),
  // Show links (decision 56, feature showLinks): teacher share links for families.
  // link is null, and review lists what to look at, until the teacher confirms with checked: true.
  createShowLink: (id: string, body: { title: string; expiresAt: number; checked?: boolean }) =>
    call<{ link: ShowLinkInfo | null; review: { path: string; line: number; rule: string; excerpt: string }[] }>(`/projects/${id}/show-links`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  showLinks: () => call<{ links: ShowLinkInfo[] }>("/show-links"),
  revokeShowLink: (token: string) => call<{ ok: true }>(`/show-links/${token}/revoke`, { method: "POST" }),
  // Live view and Pause class (Q): the teacher's tiles, pause/resume, a student's project read-only,
  // and the student's own pause state (seen: the tab is in front, which counts as activity).
  liveClasses: () => call<{ classes: LiveClass[] }>("/live/classes"),
  live: (classId: string) => call<{ class: LiveClass; students: LiveStudent[]; budget: ClassBudget; now: number }>(`/classes/${classId}/live`),
  // Helper limits (FR-28; apps/api/src/guardrails.ts): +10 messages today for one student, and the teacher's settings.
  extraTurns: (classId: string, userId: string) =>
    call<{ turnsToday: number; turnLimit: number }>(`/classes/${classId}/students/${encodeURIComponent(userId)}/extra-turns`, { method: "POST" }),
  limits: () => call<{ limits: HelperLimits; canChange: boolean }>("/settings/limits"),
  setLimits: (limits: Partial<HelperLimits>) => call<{ limits: HelperLimits }>("/settings/limits", { method: "PUT", body: JSON.stringify(limits) }),
  pauseClass: (classId: string, message: string) => call<{ class: LiveClass }>(`/classes/${classId}/pause`, { method: "POST", body: JSON.stringify({ message }) }),
  resumeClass: (classId: string) => call<{ class: LiveClass }>(`/classes/${classId}/resume`, { method: "POST" }),
  teacherProject: (projectId: string) => call<TeacherProjectView>(`/teacher/projects/${projectId}`),
  classStatus: (seen: boolean) => call<{ paused: boolean; message?: string }>(`/class/status${seen ? "?seen=1" : ""}`),
};

// Student accounts (decision 68): the class code, the class rules, and the teacher's Classes card.
export interface ClassSummary {
  id: string;
  name: string;
  joinCode: string;
  kind: ClassKind;
  /** The teacher who made it (every teacher sees every class). */
  createdBy: string;
  students: number;
  paused: boolean;
  /** "Unlock everything" for the class (D46): every tool in every lesson. */
  unlockAll?: boolean;
}
export interface ClassStudent {
  id: string;
  name: string;
  email: string;
  joinedAt: number;
  rulesAccepted: boolean;
  /** Parent/guardian emails a teacher added on the roster (decision 85; teachers only). */
  parentEmails?: string[];
  /** Their profile icon (a sprite id), or null. */
  avatar?: string | null;
  guest?: boolean;
  /** "Unlock everything" for this student (D46). */
  unlockAll?: boolean;
}
export const classesApi = {
  join: (code: string) => call<{ class: { id: string; name: string } }>("/join", { method: "POST", body: JSON.stringify({ code }) }),
  acceptRules: () => call<{ ok: true }>("/rules/accept", { method: "POST" }),
  list: () => call<{ classes: ClassSummary[] }>("/classes"),
  create: (name: string, kind: ClassKind = "class") => call<{ class: ClassSummary }>("/classes", { method: "POST", body: JSON.stringify({ name, kind }) }),
  students: (classId: string) => call<{ students: ClassStudent[] }>(`/classes/${classId}/students`),
  remove: (classId: string) => call<{ ok: true }>(`/classes/${classId}`, { method: "DELETE" }),
  removeStudent: (classId: string, userId: string) => call<{ ok: true }>(`/classes/${classId}/students/${encodeURIComponent(userId)}`, { method: "DELETE" }),
  /** Teachers only (D46): every tool in every lesson, for the whole class or one student. */
  unlockAll: (classId: string, on: boolean) => call<{ unlockAll: boolean }>(`/classes/${classId}/unlock-all`, { method: "PUT", body: JSON.stringify({ on }) }),
  unlockStudent: (classId: string, userId: string, on: boolean) =>
    call<{ unlockAll: boolean }>(`/classes/${classId}/students/${encodeURIComponent(userId)}/unlock-all`, { method: "PUT", body: JSON.stringify({ on }) }),
  /** Teachers only (decision 85): 0–2 parent/guardian emails for a student in this class; [] removes them. */
  setParents: (classId: string, userId: string, parentEmails: string[]) =>
    call<{ parentEmails: string[] }>(`/classes/${classId}/students/${encodeURIComponent(userId)}/parents`, { method: "PUT", body: JSON.stringify({ parentEmails }) }),
};

// The owner's co-teacher and guest lists (Review 14; apps/api/src/people.ts).
export interface People {
  coTeachers: string[];
  guests: string[];
}
export const peopleApi = {
  get: () => call<People & { maxGuests: number; domain: string }>("/settings/people"),
  add: (list: "coteachers" | "guests", email: string) => call<People>(`/settings/people/${list}`, { method: "POST", body: JSON.stringify({ email }) }),
  remove: (list: "coteachers" | "guests", email: string) => call<People>(`/settings/people/${list}/${encodeURIComponent(email)}`, { method: "DELETE" }),
};

export interface SurpriseTwist {
  title: string;
  pitch: string;
  message: string;
}

/** Run one agent turn, calling onEvent for each streamed event. Abort the signal to stop. */
export async function runTurn(
  projectId: string,
  body: { mode: Mode; message: string; errors?: string[]; approvedPlan?: string[]; focus?: CardFocus },
  onEvent: (e: TurnEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/turns`, {
    method: "POST",
    headers: { "content-type": "application/json", ...(await authHeaders()) },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; limit?: string; blocked?: boolean };
    const kind = res.status === 429 && err.limit ? "limit" : res.status === 422 && err.blocked ? "blocked" : undefined;
    throw new ApiError(err.error ?? `The helper couldn't start (${res.status}).`, kind);
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let cut: number;
    while ((cut = buffer.indexOf("\n\n")) !== -1) {
      const chunk = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      let event = "message";
      const data: string[] = [];
      for (const line of chunk.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
      }
      if (!data.length) continue;
      const parsed = JSON.parse(data.join("\n"));
      onEvent({ ...parsed, type: event } as TurnEvent);
    }
  }
}

/** Answers a project's shareData / getShared call (class-shared data) through the server, then replies into the frame. */
export function answerClassDataRequest(frame: Window, m: { id: number; op: "get" | "set"; key: string; value?: unknown }, from: ClassDataSource = {}): void {
  const request: Promise<unknown> = m.op === "set" ? api.setClassData(m.key, m.value, from).then(() => null) : api.classData(m.key).then((r) => r.value);
  request.then(
    (value) => frame.postMessage({ __buildlabClassData: 1, id: m.id, ok: true, value }, "*"),
    (err: Error) => frame.postMessage({ __buildlabClassData: 1, id: m.id, ok: false, error: err.message }, "*"),
  );
}

/** Answers a project's getData request (BuildLab Data) by asking the server, then replying into the frame. */
export function answerDataRequest(frame: Window, m: { id: number; source: string; params: unknown }): void {
  api
    .data(m.source, m.params)
    .then(
      ({ data }) => frame.postMessage({ __buildlabData: 1, id: m.id, ok: true, data }, "*"),
      (err: Error) => frame.postMessage({ __buildlabData: 1, id: m.id, ok: false, error: err.message }, "*"),
    );
}

/** "Lesson 4", or "Demo" for the teacher's projector demo (lesson 0). */
export const lessonLabel = (lesson: number) => (lesson === 0 ? "Demo" : `Lesson ${lesson}`);
/** "Mon, Oct 19" for a YYYY-MM-DD class date (the 🔒 on a lesson that hasn't opened yet). */
export const dayLabel = (date: string) =>
  new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`));

// The profile from the name menu (apps/api/src/profile.ts): a picked icon and fun stats.
export interface Profile {
  name: string;
  email: string;
  role: "teacher" | "student";
  avatar: string | null;
}
export interface ProfileStats {
  projects: number;
  lessonsStarted: number;
  firstProjectAt: number | null;
  lines: number;
  helperMessages: number;
  modes: { build: number; plan: number; ask: number };
  favoriteMode: "build" | "plan" | "ask" | null;
  savePoints: number;
  submissions: number;
  gallery: { shared: number; remixedByOthers: number; remixesMade: number };
  coinsSpent: number | null;
  daysActive: number;
  bestStreak: number;
  topProject: { title: string; helperMessages: number } | null;
  level: { name: string; points: number; from: number; next: { name: string; at: number } | null };
}
export const profileApi = {
  get: () => call<{ profile: Profile; stats: ProfileStats }>("/profile"),
  save: (body: { avatar: string | null }) => call<{ profile: Profile }>("/profile", { method: "PUT", body: JSON.stringify(body) }),
};

// Prompt Golf (Review 19, D53; apps/api/src/golf.ts). The hidden checks' scripts come only for the
// player's own round, at "Check my build"; the board shows First L. names, never messages (the
// teacher's best-prompt reveal is the one exception, teachers only).
export interface GolfScoreLabel {
  label: string;
  diff: number;
  kind: "ace" | "under" | "par" | "over";
}
export interface GolfHoleSummary {
  id: string;
  number: number;
  title: string;
  emoji: string;
  brief: string;
  par: number;
  checks: number;
  rounds: number;
  best: { strokes: number; words: number; seconds: number; score: GolfScoreLabel } | null;
}
export interface GolfHoleInfo {
  id: string;
  number: number;
  title: string;
  emoji: string;
  brief: string;
  par: number;
  checks: number;
  /** The finished app to watch and play. */
  files: FileMap;
}
export interface GolfAttempt {
  id: string;
  holeId: string;
  projectId: string;
  startedAt: number;
  finishedAt: number | null;
  strokes: number;
  words: number;
  par: number;
  passed: number;
  total: number;
  checksRun: number;
  seconds?: number;
  score?: GolfScoreLabel;
}
export interface GolfCheckResult {
  attempt: GolfAttempt;
  /** The first behavior that didn't pass, in plain words (only one, decision 97). */
  missing: string[];
  finished: boolean;
  newBest: boolean;
  note?: string;
}
export interface GolfBoardRow {
  rank: number;
  name: string;
  avatar: string | null;
  strokes: number;
  words: number;
  seconds: number;
  score: GolfScoreLabel;
  me: boolean;
}
export interface GolfBoard {
  classId: string | null;
  className: string;
  /** Teachers: the boards they can pick ("" is the Teachers board). */
  classes?: { id: string; name: string; demo: boolean }[];
  teacher: boolean;
  board: GolfBoardRow[];
}
export interface GolfBestPrompt {
  name: string;
  avatar: string | null;
  strokes: number;
  words: number;
  seconds: number;
  score: GolfScoreLabel;
  messages: string[];
}
const boardQuery = (classId?: string | null) => (classId === undefined || classId === null ? "" : `?classId=${encodeURIComponent(classId)}`);
export const golfApi = {
  holes: () => call<{ open: boolean; opensOn: string; holes: GolfHoleSummary[] }>("/golf"),
  hole: (holeId: string) => call<{ hole: GolfHoleInfo }>(`/golf/holes/${holeId}`),
  start: (holeId: string, resume: boolean) => call<{ attempt: GolfAttempt }>(`/golf/holes/${holeId}/attempts`, { method: "POST", body: JSON.stringify({ resume }) }),
  attempt: (id: string) => call<{ attempt: GolfAttempt }>(`/golf/attempts/${id}`),
  checks: (id: string) => call<{ checks: { id: string; script: string }[] }>(`/golf/attempts/${id}/checks`),
  report: (id: string, results: Record<string, boolean>) => call<GolfCheckResult>(`/golf/attempts/${id}/check`, { method: "POST", body: JSON.stringify({ results }) }),
  board: (holeId: string, classId?: string | null) => call<GolfBoard>(`/golf/holes/${holeId}/board${boardQuery(classId)}`),
  resetBoard: (holeId: string, classId: string | null) => call<{ ok: true }>(`/golf/holes/${holeId}/board${boardQuery(classId ?? "")}`, { method: "DELETE" }),
  bestPrompt: (holeId: string, classId?: string | null) => call<{ best: GolfBestPrompt | null }>(`/golf/holes/${holeId}/best-prompt${boardQuery(classId)}`),
};

// Bot Arena (decision 99; apps/api/src/arena.ts). Students get their own class's table and only their
// own matches' replays (a replay is a seed and moves, never code). Entries' code comes only to
// teachers, for the tournament their browser runs in the preview sandbox (arena-runner.ts).
export interface ArenaHome {
  open: boolean;
  opensOn: string;
  bot: { projectId: string; title: string; updatedAt: number } | null;
  entry: ArenaEntryInfo | null;
  latest: { id: string; createdAt: number; bots: number; rank: number | null; points: number | null } | null;
}
export interface ArenaEntryInfo {
  id: string;
  botName: string;
  /** "held": screening wasn't sure, so it waits for the teacher. */
  status: "ok" | "held";
  updatedAt: number;
}
export interface ArenaTeacherEntry extends ArenaEntryInfo {
  name: string;
  avatar: string | null;
  code: string;
  screening: string | null;
}
export interface ArenaClassPick {
  id: string;
  name: string;
  demo: boolean;
}
export interface ArenaSide {
  name: string;
  avatar: string | null;
  botName: string;
  house: boolean;
  me: boolean;
}
export interface ArenaRow extends ArenaSide {
  rank: number;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  coins: number;
  points: number;
}
export interface ArenaMatchInfo {
  id: string;
  n: number;
  a: ArenaSide;
  b: ArenaSide;
  aCoins: number;
  bCoins: number;
  winner: "a" | "b" | "draw";
}
export interface ArenaStandings {
  classId: string;
  className: string;
  classes?: ArenaClassPick[];
  teacher: boolean;
  tournament: { id: string; createdAt: number; bots: number; matches: number } | null;
  table: ArenaRow[];
  matches: ArenaMatchInfo[];
}
export interface ArenaTournamentUpload {
  classId: string;
  seed: number;
  bots: string[];
  matches: { a: string; b: string; replay: Replay }[];
}
export const arenaApi = {
  home: () => call<ArenaHome>("/arena"),
  bot: () => call<{ projectId: string; created: boolean }>("/arena/bot", { method: "POST", body: "{}" }),
  enter: (projectId: string, botName: string) => call<{ entry: ArenaEntryInfo }>("/arena/entries", { method: "POST", body: JSON.stringify({ projectId, botName }) }),
  entries: (classId?: string | null) => call<{ classId: string; className: string; classes: ArenaClassPick[]; entries: ArenaTeacherEntry[] }>(`/arena/entries${boardQuery(classId)}`),
  approve: (id: string) => call<{ ok: true }>(`/arena/entries/${id}/approve`, { method: "POST" }),
  remove: (id: string) => call<{ ok: true }>(`/arena/entries/${id}`, { method: "DELETE" }),
  saveTournament: (t: ArenaTournamentUpload) => call<{ id: string; bots: number; matches: number }>("/arena/tournaments", { method: "POST", body: JSON.stringify(t) }),
  standings: (classId?: string | null) => call<ArenaStandings>(`/arena/standings${boardQuery(classId)}`),
  match: (id: string) => call<{ match: ArenaMatchInfo & { replay: Replay } }>(`/arena/matches/${id}`),
};
